import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Paper,
  Card,
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  TextField,
  IconButton,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CompactLevelCard } from '../components/CompactLevelCard';
import { TradingViewNewsCalendar } from '../components/TradingViewNewsCalendar';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  CheckCircle as CheckIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as VerifyIcon,
  Psychology as PsychologyIcon,
  AutoAwesome as AutoAwesomeIcon,
  Speed as SpeedIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';

// TradingView Ticker Tape Widget Component (same as Market Intelligence)
const TradingViewTicker: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { settings } = useUserSettings();

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !settings.tickerSymbols.length) return;

    // Clear any existing content
    container.innerHTML = `
      <div class="tradingview-widget-container__widget"></div>
    `;

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js';
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => {
      // Script loaded successfully
    };
    script.onerror = () => {
      console.warn('Failed to load TradingView ticker tape widget');
    };
    
    // Convert settings symbols to ticker format
    const tickerSymbols = settings.tickerSymbols.map(symbol => ({
      proName: symbol.proName,
      title: symbol.title || symbol.description || symbol.proName.split(':')[1] || symbol.proName
    }));
    
    script.innerHTML = JSON.stringify({
      symbols: tickerSymbols,
      showSymbolLogo: true,
      isTransparent: false,
      largeChartUrl: "",
      displayMode: "adaptive",
      colorTheme: "dark",
      locale: "en"
    });

    const widgetContainer = container.querySelector('.tradingview-widget-container__widget');
    if (widgetContainer) {
      widgetContainer.appendChild(script);
    }

    return () => {
      // Cleanup on unmount
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [settings.tickerSymbols]);

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        mb: 4, 
        borderRadius: 0,
        overflow: 'hidden',
       
         
      }}
    >
      <div className="tradingview-widget-container" ref={containerRef}>
        <div className="tradingview-widget-container__widget"></div>
      </div>
    </Paper>
  );
};

// Trading Account Affiliation Component
const TradingAccountPrompts: React.FC<{ userProfile: any; onVerifyXM: () => void; xmVerificationStatus: string; xmVerificationMessage: string }> = ({ 
  userProfile, 
  onVerifyXM,
  xmVerificationStatus,
  xmVerificationMessage 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Check if user has trading account numbers saved
  const hasXmAccount = userProfile?.xmAccountNumber && userProfile?.xmAccountNumber.trim();
  const hasWeltradeAccount = userProfile?.weltradeAccountNumber && userProfile?.weltradeAccountNumber.trim();
  const hasDerivAccount = userProfile?.derivAccountNumber && userProfile?.derivAccountNumber.trim();
  
  // Determine which broker cards to show (only show cards for brokers without saved account numbers)
  const showXmCard = !hasXmAccount;
  const showWeltradeCard = !hasWeltradeAccount;
  const showDerivCard = !hasDerivAccount;
  
  // If all accounts are set up, don't show anything
  if (!showXmCard && !showWeltradeCard && !showDerivCard) {
    return null;
  }

  return (
    <Box>
      <Typography variant="body2" gutterBottom sx={{ textAlign: 'center', mb: 2, fontWeight: 600, color: 'text.secondary' }}>
        🚀 Partner Brokers
      </Typography>

      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5
        }}
      >
        {/* XM - Only show if no XM account number saved */}
        {showXmCard && (
          <Card 
            elevation={1}
            sx={{ 
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
              },
              background: 'rgba(30, 41, 59, 0.4)',
              border: '1px solid rgba(66, 165, 245, 0.1)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                  XM Global
                </Typography>
              </Box>
              
              <Typography variant="caption" sx={{ color: 'rgba(203, 213, 225, 0.8)', mb: 1.5, display: 'block' }}>
                Partner Code: T1FXTEAM
              </Typography>
              
              <Button
                variant="contained"
                fullWidth
                size="small"
                sx={{
                  backgroundColor: 'rgba(59, 130, 246, 0.8)',
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  py: 0.5,
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(59, 130, 246, 0.9)',
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                    color: '#000',
                  }
                }}
                endIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem' }} />}
                href="https://affs.click/bwvBB"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Account
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Weltrade - Only show if no Weltrade account number saved */}
        {showWeltradeCard && (
          <Card 
            elevation={1}
            sx={{ 
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
              },
              background: 'rgba(30, 41, 59, 0.4)',
              border: '1px solid rgba(99, 102, 241, 0.1)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Weltrade
                </Typography>
              </Box>
              
              <Typography variant="caption" sx={{ color: 'rgba(203, 213, 225, 0.8)', mb: 1.5, display: 'block' }}>
                Partner Code: CX40955
              </Typography>
              
              <Button
                variant="contained"
                fullWidth
                size="small"
                sx={{
                  backgroundColor: 'rgba(99, 102, 241, 0.8)',
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  py: 0.5,
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.9)',
                    borderColor: 'rgba(99, 102, 241, 0.3)',
                    color: '#000',
                  }
                }}
                endIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem' }} />}
                href="https://track.gowt.me/visit/?bta=40955&brand=weltrade"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Account
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Deriv - Only show if no Deriv account number saved */}
        {showDerivCard && (
          <Card 
            elevation={1}
            sx={{ 
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
              },
              background: 'rgba(30, 41, 59, 0.4)',
              border: '1px solid rgba(156, 163, 175, 0.1)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Deriv
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                fullWidth
                size="small"
                sx={{
                  backgroundColor: 'rgba(239, 68, 68, 0.8)',
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  py: 0.5,
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.9)',
                    borderColor: 'rgba(239, 68, 68, 0.3)',
                    color: '#000',
                  }
                }}
                endIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem' }} />}
                href="https://track.deriv.com/_XPeZMTGp7aMKqFKZ7JdnQ2Nd7ZgqdRLk/1/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Account
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

// Competition Ticker Component
const CompetitionTicker: React.FC = () => {
  const theme = useTheme();
  const [topTraders, setTopTraders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch leaderboard data from Firestore
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching leaderboard data from Firestore...');
        
        // Get current month in YYYY-MM format to match the document structure
        const currentMonth = new Date().toISOString().slice(0, 7); // e.g., "2025-01"
        console.log('Current month key:', currentMonth);
        
        // Get the current month's leaderboard document
        const leaderboardDoc = await getDoc(doc(db, 'leaderboardData', currentMonth));
        
        console.log('Leaderboard document exists:', leaderboardDoc.exists());
        
        if (!leaderboardDoc.exists()) {
          console.log('No leaderboard data found for current month');
          setTopTraders([]);
          return;
        }
        
        const docData = leaderboardDoc.data();
        console.log('Leaderboard document data:', docData);
        
        // Extract the leaderboard array and get top 5
        const leaderboard = docData?.leaderboard || [];
        console.log('Leaderboard array length:', leaderboard.length);
        
        // Take top 5 and format for the ticker
        const topFiveTraders = leaderboard.slice(0, 5).map((trader: any, index: number) => ({
          id: `${currentMonth}-${trader.loginId}`,
          rank: index + 1, // Use index + 1 for display ranking
          name: trader.displayName || `Trader ${trader.loginId}`,
          points: trader.totalPoints || 0,
          ...trader
        }));
        
        console.log('Processed top 5 traders:', topFiveTraders);
        setTopTraders(topFiveTraders);
      } catch (err) {
        console.error('Error fetching leaderboard data:', err);
        setError('Failed to load leaderboard data');
        
        // Fallback to empty array or show error message
        setTopTraders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboardData();
  }, []);

  const getRankIcon = (rank: number) => {
    return `#${rank}`;
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1: return '#FFD700';
      case 2: return '#C0C0C0';
      case 3: return '#CD7F32';
      default: return theme.palette.primary.main;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Paper
        elevation={2}
        sx={{
          mt: 4,
          p: 2,
          background: 'linear-gradient(90deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 50%, rgba(30, 41, 59, 0.95) 100%)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255, 215, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={20} sx={{ color: '#FFD700', mr: 2 }} />
        <Typography variant="body2" sx={{ color: '#FFD700' }}>
          Loading competition data...
        </Typography>
      </Paper>
    );
  }

  // Show error or empty state
  if (error) {
    return (
      <Paper
        elevation={2}
        sx={{
          mt: 4,
          p: 2,
          background: 'linear-gradient(90deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 50%, rgba(30, 41, 59, 0.95) 100%)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255, 215, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: '#ff6b6b' }}>
          {error}
        </Typography>
      </Paper>
    );
  }

  // Don't render if no traders
  if (!topTraders.length) {
    return (
      <Paper
        elevation={2}
        sx={{
          mt: 4,
          p: 2,
          background: 'linear-gradient(90deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 50%, rgba(30, 41, 59, 0.95) 100%)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255, 215, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: '#FFD700' }}>
          🏆 No competition data available yet
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        mt: 4,
        background: 'linear-gradient(90deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 50%, rgba(30, 41, 59, 0.95) 100%)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(255, 215, 0, 0.2)',
        py: { xs: 1, md: 1.5 },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          animation: { 
            xs: 'continuousScroll 15s linear infinite', // Comfortable mobile speed
            md: 'continuousScroll 45s linear infinite'  // Slower, more readable desktop speed
          },
          whiteSpace: 'nowrap',
          '@keyframes continuousScroll': {
            '0%': { transform: 'translateX(100vw)' },
            '100%': { transform: 'translateX(-200%)' },
          },
        }}
      >
        {/* Create the complete content set */}
        {[...Array(2)].map((_, setIndex) => (
          <React.Fragment key={`content-set-${setIndex}`}>
            {/* Competition Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 2, md: 6 } }}>
              <TrophyIcon sx={{ color: '#FFD700', mr: { xs: 0.5, md: 1 }, fontSize: { xs: '0.9rem', md: '1.2rem' } }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  color: '#FFD700',
                  mr: { xs: 0.5, md: 2 },
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                }}
              >
                VOLUME MASTERS - {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase()}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 215, 0, 0.8)',
                  mr: { xs: 1.5, md: 4 },
                  fontSize: { xs: '0.6rem', md: '0.75rem' },
                }}
              >
                R50K • Gold • BTC • Silver
              </Typography>
            </Box>

            {/* Top Traders */}
            {topTraders.map((trader) => (
              <Box
                key={`${setIndex}-${trader.id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mr: { xs: 1.5, md: 6 },
                  px: { xs: 0.8, md: 2 },
                  py: { xs: 0.3, md: 0.5 },
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 2,
                  border: `1px solid ${getRankColor(trader.rank)}30`,
                  minWidth: 'fit-content',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: getRankColor(trader.rank),
                    fontWeight: 'bold',
                    mr: { xs: 0.5, md: 1 },
                    minWidth: { xs: '18px', md: '24px' },
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                  }}
                >
                  {getRankIcon(trader.rank)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    mr: { xs: 0.5, md: 1 },
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                  }}
                >
                  {trader.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 215, 0, 0.9)',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                  }}
                >
                  {trader.points.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Paper>
  );
};

export const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for user profile and XM verification
  const [userProfile, setUserProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [xmAccountNumber, setXmAccountNumber] = useState('');
  const [xmVerificationStatus, setXmVerificationStatus] = useState<'unchecked' | 'checking' | 'affiliated' | 'not-affiliated' | 'error'>('unchecked');
  const [xmVerificationMessage, setXmVerificationMessage] = useState('');

  // Load user profile
  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserProfile(data);
          setXmAccountNumber(data.xmAccountNumber || '');
          
          // Set initial verification status
          if (data.xmAffiliationVerified && data.xmAccountNumber) {
            setXmVerificationStatus('affiliated');
            setXmVerificationMessage('✅ Account is affiliated with T1 FX Team');
          }
        }
      } catch (error) {
        console.error('Failed to load profile data:', error);
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfile();
  }, [currentUser]);

  // XM Account verification function
  const handleVerifyXM = async () => {
    if (!userProfile?.xmAccountNumber || userProfile.xmAccountNumber.trim() === '') {
      setXmVerificationStatus('error');
      setXmVerificationMessage('No XM account number found. Please add it in your profile settings.');
      return;
    }

    setXmVerificationStatus('checking');
    setXmVerificationMessage('Checking affiliation...');

    try {
      const response = await fetch(
        `https://us-central1-t1-fx-team.cloudfunctions.net/checkAffiliation?accountNumber=${encodeURIComponent(userProfile.xmAccountNumber)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        if (data.isAffiliated) {
          setXmVerificationStatus('affiliated');
          setXmVerificationMessage('✅ Account is affiliated with T1 FX Team');
          
          // Save affiliation status
          if (currentUser) {
            await updateDoc(doc(db, 'users', currentUser.uid), {
              xmAffiliationVerified: true,
              xmAffiliationVerifiedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            
                         // Update local state
             setUserProfile((prev: any) => ({ 
               ...prev, 
               xmAffiliationVerified: true,
               xmAffiliationVerifiedAt: new Date()
             }));
            
            // Notify other components
            window.dispatchEvent(new CustomEvent('userLevelUpdated'));
          }
        } else {
          setXmVerificationStatus('not-affiliated');
          setXmVerificationMessage('❌ Account is not affiliated with T1 FX Team');
        }
      } else {
        setXmVerificationStatus('error');
        setXmVerificationMessage('Error checking affiliation: ' + (data.message || 'Unknown error'));
      }
    } catch (error: any) {
      setXmVerificationStatus('error');
      setXmVerificationMessage('Network error: Unable to check affiliation');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="xl">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Welcome Section */}
          <motion.div variants={itemVariants}>
            <Typography
              variant={isMobile ? 'h4' : 'h2'}
              component="h1"
              gutterBottom
              sx={{ mb: 1, textAlign: 'center' }}
            >
              Welcome back, {currentUser?.displayName?.split(' ')[0] || 'Trader'}! 🎯
            </Typography>
            
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, textAlign: 'center', maxWidth: '600px', mx: 'auto' }}
            >
              Your trading command center. Access all the tools and resources you need to make informed trading decisions.
            </Typography>
          </motion.div>

          {/* TradingView Ticker Widget */}
          <motion.div variants={itemVariants}>
            <TradingViewTicker />
          </motion.div>

          {/* Main Dashboard Grid Layout */}
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: { 
                xs: '1fr', // Mobile: single column
                md: '1fr 2fr 1fr' // Desktop: left sidebar, main content, right sidebar
              },
              gap: 3,
              alignItems: 'start',
              mt: 4
            }}
          >
            {/* Left Sidebar - News Calendar */}
            <Box sx={{ display: { xs: 'block', md: 'block' } }}>
              <motion.div variants={itemVariants}>
                <TradingViewNewsCalendar />
              </motion.div>
            </Box>

            {/* Main Content Area */}
            <Box sx={{ minHeight: '400px', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* AI Analyses Promotion */}
              <motion.div variants={itemVariants}>
                <Paper 
                  elevation={3}
                  sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 160, 0, 0.05) 100%)',
                    border: '2px solid rgba(255, 215, 0, 0.3)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, transparent 30%, rgba(255, 215, 0, 0.03) 50%, transparent 70%)',
                      pointerEvents: 'none',
                    }
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <PsychologyIcon sx={{ fontSize: 40, color: '#FFD700', mr: 1 }} />
                      <AutoAwesomeIcon sx={{ fontSize: 24, color: '#FFA000', animation: 'pulse 2s infinite' }} />
                    </Box>
                    
                    <Typography 
                      variant={isMobile ? 'h5' : 'h4'} 
                      gutterBottom 
                      sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(45deg, #FFD700 30%, #FFA000 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 2
                      }}
                    >
                      🧠 AI-Powered Market Analysis
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: '600px', mx: 'auto', lineHeight: 1.6 }}>
                      Get instant, professional-grade technical analysis powered by AI. 
                      Analyze any chart with advanced pattern recognition, sentiment analysis, and personalized trading recommendations.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mb: 3 }}>
                      <Chip 
                        icon={<SpeedIcon />}
                        label="Real-time Analysis" 
                        size="small"
                        sx={{ 
                          backgroundColor: 'rgba(76, 175, 80, 0.2)',
                          color: '#4caf50',
                          fontWeight: 'bold'
                        }} 
                      />
                      <Chip 
                        icon={<PsychologyIcon />}
                        label="AI-Powered" 
                        size="small"
                        sx={{ 
                          backgroundColor: 'rgba(156, 39, 176, 0.2)',
                          color: '#9c27b0',
                          fontWeight: 'bold'
                        }} 
                      />
                      <Chip 
                        label="Fundamental Analysis" 
                        size="small"
                        sx={{ 
                          backgroundColor: 'rgba(33, 150, 243, 0.2)',
                          color: '#2196f3',
                          fontWeight: 'bold'
                        }} 
                      />
                      <Chip 
                        label="Technical Analysis" 
                        size="small"
                        sx={{ 
                          backgroundColor: 'rgba(255, 152, 0, 0.2)',
                          color: '#ff9800',
                          fontWeight: 'bold'
                        }} 
                      />
                    </Box>
                    
                    <Button 
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/ai-analyses')}
                      startIcon={<PsychologyIcon />}
                      endIcon={<AutoAwesomeIcon />}
                      sx={{
                        background: 'linear-gradient(45deg, #FFD700 30%, #FFA000 90%)',
                        color: '#000',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        px: 4,
                        py: 1.5,
                        borderRadius: 25,
                        boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #FFA000 30%, #FFD700 90%)',
                          boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Try AI Analysis Now
                    </Button>
                    
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                      💡 Powered by AI • Professional Trading Insights
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>

              {/* Trading Hub */}
              <motion.div variants={itemVariants}>
                <Paper 
                  elevation={2}
                  sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    borderRadius: 2,
                    background: theme.palette.background.paper
                  }}
                >
                  <Typography variant="h5" gutterBottom>
                    🚀 Your Trading Hub
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Monitor your progress and stay updated with market events.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      onClick={() => navigate('/chart')}
                    >
                      View Charts
                    </Button>
                    <Button 
                      variant="outlined" 
                      onClick={() => navigate('/trading-journal')}
                    >
                      Trading Journal
                    </Button>
                    <Button 
                      variant="outlined" 
                      onClick={() => navigate('/feedback')}
                      sx={{
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                          borderColor: theme.palette.primary.light,
                        }
                      }}
                    >
                      Share Feedback
                    </Button>
                  </Box>
                </Paper>
              </motion.div>
            </Box>

            {/* Right Sidebar - Level Card and Partner Brokers */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <motion.div variants={itemVariants}>
                <Box sx={{ position: 'relative' }}>
                  <CompactLevelCard />
                </Box>
              </motion.div>
              
              {/* Trading Account Prompts - Desktop */}
              {!profileLoading && (
                <motion.div variants={itemVariants}>
                  <Box sx={{ mt: 3 }}>
                    <TradingAccountPrompts 
                      userProfile={userProfile}
                      onVerifyXM={handleVerifyXM}
                      xmVerificationStatus={xmVerificationStatus}
                      xmVerificationMessage={xmVerificationMessage}
                    />
                  </Box>
                </motion.div>
              )}
            </Box>
          </Box>

          {/* Mobile Partner Brokers Section */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 4 }}>
            {!profileLoading && (
              <motion.div variants={itemVariants}>
                <TradingAccountPrompts 
                  userProfile={userProfile}
                  onVerifyXM={handleVerifyXM}
                  xmVerificationStatus={xmVerificationStatus}
                  xmVerificationMessage={xmVerificationMessage}
                />
              </motion.div>
            )}
          </Box>

          {/* Competition Ticker - Above Footer/Disclaimer */}
          <motion.div variants={itemVariants}>
            <CompetitionTicker />
          </motion.div>

        </motion.div>
      </Container>
    </Box>
  );
}; 